/*jslint node: true, browser: true*/
"use strict";

var ko = require("pli/knockout");
var pliObservable = require("pli/forms-knockoutcontrols");

var createContactPercentageFactory = require("../document/contactPercentageViewModel");
var selectionForm = require("../common-selectionform");
var viewModelInit = require("../document/viewModelInitExtensions");


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Private
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//---------------------------------------
function createFlagObserver(name) {
    /// <summary>
    /// Create a function that can be used
    /// to observe isActive or isRental flags
    /// and update owner.currentActive or
    /// owner.currentRental appropriately.
    /// </summary>
    /// <remarks>
    /// Returned observer function expects
    /// "this" to be pointing to the DOI
    /// that owns the observed flag.
    /// </remarks>
    return function doiToggleObserver(newFlagValue) {
        var dois = this.extras.owner,
            oldCurrent = dois["current" + name]();
        if (newFlagValue) { // toggling "false" => "true"
            if (oldCurrent !== this) {
                dois["current" + name](this);
                if (oldCurrent) {
                    oldCurrent["is" + name](false);
                }
            }
        } else { // toggling "true" => "false"
            if (oldCurrent === this) {
                dois["current" + name](null);
            }
        }
    };
}


//-------------------------------------------
function selectDois(doisArray, link) {
    /// <summary>
    /// Show dois selection form and either
    /// copy (if "link" is falsy) or link (if "link" is truthy)
    /// selected dois to the specified array.
    /// </summary>
    selectionForm.open({
        getDetailsApi: "Geo-CopyLinkDocument-GetDetails",
        getDetailsDataBuilder: function (items) { // needed to adapt request for Geo-CopyLinkDocument-GetDetails
            var guidIndex = items[0].length - 1;
            return {
                documentType: "Doi",
                actionType: (link ? "Link" : "Copy"),
                copyLinkFrom: ko.utils.arrayMap(items, function (item) {
                    return {
                        name: item[0],
                        guid: item[guidIndex]
                    };
                })
            };
        },
        multiSelect: true,
        onAcceptedContext: doisArray,
        onAccepted: function (newDois) {
            if (!link) { // if copying, reset all guid-s (so that save will create a new, copied content)
                newDois = selectionForm.utils.resetGuids(newDois);
            } else if (newDois.length > 0) { // ensure only the first new line is marked active
                newDois[0].isNewLink = true;
            }
            selectionForm.utils.pushObjectItems({
                targetArray: doisArray,
                records: newDois,
                getIdentity: (!link ? null : function (royalty) { return royalty.guid(); })
            });
            // added multiple new items, so we need to undo the selection of the first that was added
            if (newDois.length > 1 && doisArray().length === newDois.length) {
                var firstDoi = doisArray.peek()[0];
                firstDoi.isActive(false);
                firstDoi.isRental(false);
            }
        },
        searchApi: "Geo-Document-SearchDois",
        text: {
            acceptButton: (link ? "Link" : "Copy"),
            iconClass: (link ? "sprite-link-16" : "sprite-copy-16"),
            examples: [
                "<strong>File Numbers</strong>:<br>C-12 will find C-123, C-1234, etc.<br>M001 will find M001, M0019, etc.",
                "<strong>Areas</strong>:<br>river will find GHOST RIVER, TWO RIVERS, etc.",
                "<strong>Lands</strong>:<br>68 17 4 will find Twp 68-Rge 17-W4M<br>94 h 13 c will find 94-H-11-C<br>Note: Sections are not searched",
                "<strong>Combinations</strong>:<br>C, lloyd will find contracts (starting with C) in the Lloydminster area"
            ],
            title: (link ? "Link Divisions of Interest" : "Copy Divisions of Interest")
        }
    });
}



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Doi class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//---------------------------------------
function Doi(factories, owner) {
    /// <summary>
    /// Define viewmodel.
    /// </summary>
    if (window.console && window.console.group) { window.console.group("Doi.ctor", factories); }

    this.description = pliObservable();
    this.guid = ko.observable(null);
    this.id = this.doiName = pliObservable(null, null, "required");
    this.interestLines = factories.doiLines.createObservableArray();
    this.isActive = ko.observable(false);
    this.isRental = ko.observable(false);
    this.link = ko.observable(null);

    // extras
    this.extras = function DoiExtras() { return; };
    this.extras.errorsCount = ko.computed(this._computeErrorsCount, this);
    this.extras.owner = owner;
    this.extras.globalIsReadOnly = ko.observable().subscribeTo("state.isReadOnly", true);
    this.extras.isReadOnly = ko.computed(this._computeReadOnlyFlag, this); // has to be after stateIsReady
    this.extras.dependencies = ko.observableArray();
    this.extras.dependencyHint = "  You may set the status to 'Inactive'. All Related Document entries and links (DOI & Royalty) must be removed, and any changes must be saved, before this Split may be deleted.";

    // bound methods
    this.toggleIsActive = this._toggleFlag.bind(this.isActive);
    this.toggleIsRental = this._toggleFlag.bind(this.isRental);

    if (window.console && window.console.groupEnd) { window.console.groupEnd(); }
} // end function Doi


//---------------------------------------
Doi.prototype.applyServerCalculationResult = function applyDoiResult(context) {
    context.visit(this.interestLines, "interestLines");
};


//---------------------------------------
Doi.prototype.buildServerCalculationData = function buildDoiData(context) {
    return {
        interestLines: context.visit(this.interestLines),
        isActive: this.isActive(),
        isRental: this.isRental()
    };
};


//---------------------------------------
Doi.prototype.completeViewModel = function completeDoi() {
    /// <summary>
    /// Finish defining viewmodel.
    /// </summary>

    // only automatically activate newly added owned or linked dois
    // verify new owned dois by checking for no `guid`
    // verify new linked dois by checking for `extras._isNewLink`
    if (this.extras.owner().length === 0 && (!this.guid() || (this.link() && this.extras._isNewLink))) {
        this.isActive(true);
        this.isRental(true);
    }
    this.extras._isActiveSubscription = this.isActive.subscribe(this._isActiveObserver, this);
    this._isActiveObserver(this.isActive()); // synchronize observer with current value
    this.extras._isRentalSubscription = this.isRental.subscribe(this._isRentalObserver, this);
    this._isRentalObserver(this.isRental()); // synchronize observer with current value
    this.extras._onDisposeResetFlagObservers = { // this object makes sure _resetFlagObservers is run when this item is disposed
        doi: this,
        dois: this.extras.owner,
        dispose: this._resetFlagObservers
    };
};


//---------------------------------------
Doi.prototype.initGridArray = function initDoiGridArray(gridArray) {
    /// <summary>
    /// Add extra methods to the specified DOIs
    /// grid array (that has just been created).
    /// </summary>
    gridArray.copyItems = gridArray.factory.factories.secureAction(Doi.prototype._copyItems, gridArray);
    gridArray.linkItems = gridArray.factory.factories.secureAction(Doi.prototype._linkItems, gridArray);
    gridArray.deleteAndCloseCurrent = gridArray.factory.factories.secureAction(gridArray.deleteAndCloseCurrent, gridArray, gridArray.factory.factories.checkForDependencies.bind(gridArray));
    gridArray.openNew = gridArray.factory.factories.secureAction(gridArray.openNew, gridArray);

    gridArray.currentActive = ko.observable(null);
    gridArray.currentRental = ko.observable(null);
};



//-------------------------------------------
Doi.prototype.loadViewModel = function loadDoi(record) {
    /// <summary>
    /// Populate viewmodel with data.
    /// </summary>
    if (window.console && window.console.group) { window.console.group("Doi.loadViewModel", record); }

    if (!record) { record = {}; }

    this.description(record.description || "");
    this.guid(record.guid || null);
    this.id.load(record.id);
    this.interestLines.loadViewModel(record.interestLines);
    this.isActive(!!record.isActive);
    this.isRental(!!record.isRental);
    this.link(record.link || null);
    if (record.isNewLink) { this.extras._isNewLink = true; }
    this.interestLines.isReadOnly(!!this.link());

    this.extras.dependencies(record.dependencies || []);

    if (window.console && window.console.groupEnd) { window.console.groupEnd(); }
};


//---------------------------------------
Doi.prototype._computeErrorsCount = function doiComputeErrorsCount() {
    var totalError = (this.interestLines().length > 0 && this.interestLines.totalPercentage.invalid()) ? 1 : 0;
    return this._computeErrorsCountFlat() + this.interestLines.errorsCount() + totalError;
};


//---------------------------------------
Doi.prototype._computeErrorsCountFlat = viewModelInit.computeErrorsCount;


//---------------------------------------
Doi.prototype._computeReadOnlyFlag = function computeDoiReadOnlyFlag() {
    var readonly = this.extras.globalIsReadOnly(), link = this.link();
    return readonly || link;
};


//---------------------------------------
Doi.prototype._copyItems = function copyDois() {
    /// <summary>
    /// Copy dois from other documents. Shows
    /// a selection form that allows user to select
    /// and accept dois to copy.
    /// </summary>
    /// <remarks>
    /// Expects "this" to be a grid observable
    /// array that creates and stores dois.
    /// </remarks>
    selectDois(this, false); // false => copy
};


//---------------------------------------
Doi.prototype._isActiveObserver = createFlagObserver("Active");


//---------------------------------------
Doi.prototype._isRentalObserver = createFlagObserver("Rental");


//---------------------------------------
Doi.prototype._linkItems = function linkDois() {
    /// <summary>
    /// Link dois from other documents. Shows
    /// a selection form that allows user to select
    /// and accept dois to link.
    /// </summary>
    /// <remarks>
    /// Expects "this" to be a grid observable
    /// array that creates and stores dois.
    /// </remarks>
    selectDois(this, true); // true => link
};


//---------------------------------------
Doi.prototype._resetFlagObservers = function resetDoiFlagObservers() {
    /// <summary>
    /// If an item that is current active
    /// or rental working interest is disposed (e.g. deleted),
    /// this method resets owner.currentActive
    /// and/or owner.currentRental to null.
    /// </summary>
    /// <remarks>
    /// Expects "this" to point to _onDisposeResetFlagObservers object.
    /// </remarks>
    if (this.dois) {
        if (this.dois.currentActive() === this.doi) { this.dois.currentActive(null); }
        if (this.dois.currentRental() === this.doi) { this.dois.currentRental(null); }
        this.doi = null;
        this.dois = null;
    }
};


//---------------------------------------
Doi.prototype._toggleFlag = function toggleDoiFlag() {
    /// <summary>
    /// Toggle the specified flag.
    /// </summary>
    /// <remarks>
    /// Expects "this" to point to the flag to toggle.
    /// </remarks>
    this(!this());
};


//---------------------------------------
Doi.prototype.itemComparer = null; // disable client-side sorting


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Exports
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

module.exports = viewModelInit.standardFactoryCreatorFor(
    Doi,
    "dois",
    function createExtraDoiFactories(rootId, calculateTotalPercentageOnClient) {
        rootId = rootId.replace("_interestLines", ""); // adjust rootId (remove interestLines)
        return {
            doiLines: createContactPercentageFactory(rootId, "interestLines", calculateTotalPercentageOnClient)
        };
    }
);



//////////////////
// WEBPACK FOOTER
// ./app/document-doi/doiViewModel.js
// module id = 550
// module chunks = 49